import type { JsonValue } from './types';

export const addressKeys = ['line1', 'line2', 'city', 'region', 'postalCode', 'country'] as const;
export type StructuredAddress = Partial<Record<(typeof addressKeys)[number], string>>;

export function isStructuredAddress(value: unknown): value is StructuredAddress {
	return (
		!!value &&
		typeof value === 'object' &&
		!Array.isArray(value) &&
		Object.entries(value).every(([key, item]) => addressKeys.some((allowed) => allowed === key) && typeof item === 'string')
	);
}

export function addressParts(value: StructuredAddress) {
	const clean = (key: (typeof addressKeys)[number]) => (value[key] ?? '').trim().replace(/[,\s]+$/u, '');
	const street = [clean('line1'), clean('line2')].filter(Boolean).join(', ');
	const city = clean('city');
	const region = clean('region');
	const postalCode = clean('postalCode');
	const country = clean('country');
	const locality = region
		? [city, [region, postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
		: [city, postalCode].filter(Boolean).join(' ');
	// Domestic addresses need no country suffix; never infer a missing country.
	const countryLine = /^(us|usa|u\.?s\.?a?\.?|united states(?: of america)?)$/iu.test(country) ? '' : country;
	return { street, city, region, postalCode, country: countryLine, locality };
}

export function formatAddress(value: JsonValue | undefined, inline = false): string {
	if (typeof value === 'string') return inline ? value.trim().replace(/\s*\r?\n\s*/gu, ', ') : value.trim();
	if (!isStructuredAddress(value)) return '';
	const { street, locality, country } = addressParts(value);
	return [street, locality, country].filter(Boolean).join(inline ? ', ' : '\n');
}

export function validateAddress(value: unknown): boolean {
	if (typeof value === 'string') return value.length <= 10_000;
	// Address components are single-line text, not document markup or control sequences.
	// eslint-disable-next-line no-control-regex
	return isStructuredAddress(value) && Object.values(value).every((part) => part.length <= 500 && !/[\r\n\u0000-\u001f]/u.test(part));
}
